import { Fab, Grid, Typography } from '@mui/material';
import * as queryString from 'query-string';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { CarSelectorAppContext } from '../../contexts/CarSelectorContext';
import { SearchError } from '../../enums/searchError';
import { CarModel } from '../../models';
import { IUrlParameters } from '../../models/urlParameters';
import { DropdownSelect } from '../filter/dropdownSelect';
import { ISelectOptionProps } from '../filter/filterSelectBase';
import styles from './styles.scss';

type SearchFormProps = RouteComponentProps & {
	formClassName?: string;
	onSubmit: (productionPeriod: string, market: string, language: string, hasDigitalManuals: boolean, afterSubmit?: () => void) => void;
	onProductionPeriodChange: (isMarketAvailable: boolean) => void;
	onError?: (error: SearchError) => void;
	filterControlClassName?: string;
	filterControllFilledClassName?: string;
	model: CarModel;
	isDetailPage?: boolean;
	noMarket?: boolean;
};

type SearchFormState = {
	editions: ISelectOptionProps[];
	markets: ISelectOptionProps[];
	languages: ISelectOptionProps[];
	hasProductionDigitalManuals: boolean;
	edition: string;
	market: string;
	language: string;
	formErrorType: SearchError;
	formErrorText: string;
};

class SearchForm extends React.Component<SearchFormProps, SearchFormState> {
	static contextType = CarSelectorAppContext;
	context: React.ContextType<typeof CarSelectorAppContext>;

	constructor(props: SearchFormProps) {
		super(props);
		this.state = {
			editions: [],
			languages: [],
			markets: [],
			hasProductionDigitalManuals: false,
			edition: null,
			market: null,
			language: null,
			formErrorType: SearchError.NoError,
			formErrorText: null,
		};
	}

	componentWillReceiveProps(nextProps: SearchFormProps) {
		if (this.props.model != null && nextProps.model != null && this.props.model.modelType !== nextProps.model.modelType) {
			this.context.setSearchData(null, null, null, false);
		}
	}

	componentWillUnmount() {
		this.context.clearUrlParameters();
	}

	componentDidMount() {
		this.setState(
			{
				edition: this.props.model.modelType === this.context.modelType ? this.context.edition : null,
				market: this.props.model.modelType === this.context.modelType ? this.context.market : null,
				language: this.props.model.modelType === this.context.modelType ? this.context.language : null,
				hasProductionDigitalManuals: this.props.model.modelType === this.context.modelType ? this.context.hasProductionDigitalManuals : null,
			},
			() => {
				const parseParams = queryString.parse(this.props.location.search);
				const params = parseParams as object as IUrlParameters;

				if (params.edition && !this.state.edition) {
					this.context.setLoading(true);
					this.setState(
						{
							edition: params.edition,
							market: params.market,
							language: params.manualLang,
							hasProductionDigitalManuals: !!params.market,
						},
						() => {
							this.context.initData(params, this.loadDefaultValues);
						}
					);
				} else {
					if (!params.edition) {
						this.props.onError?.(SearchError.EmptyEditionParam);
					}
					this.loadDefaultValues();
				}
			}
		);
	}

	loadDefaultValues = async () => {
		if (!this.props.model) {
			return;
		}

		try {
			const editions = await this.context.getEditions(this.props.model.modelType);
			const editionOptions = editions.map<ISelectOptionProps>((x) => ({
				isSelected: this.state.edition && (this.state.edition === x.code || this.state.edition === x.omsCode),
				key: x.code,
				value: x.title,
			}));

			const selectedEditionModel =
				this.state.edition != null ? editions.find((x) => x.code === this.state.edition || x.omsCode === this.state.edition) : null;

			this.setState({
				editions: editionOptions,
				hasProductionDigitalManuals: selectedEditionModel ? selectedEditionModel.hasDigitalManuals : this.state.hasProductionDigitalManuals,
			});

			this.props.onProductionPeriodChange(!selectedEditionModel ? true : selectedEditionModel.hasDigitalManuals);

			// Check set edition parameter when params in url (Detail)
			if (
				this.context.urlParameters &&
				this.context.urlParameters.model &&
				(!this.state.edition || (this.state.edition && editionOptions.length > 0 && editionOptions.find((x) => x.isSelected) == null))
			) {
				throw SearchError.EditionNotFound;
			}

			// Check market parameter set for DM edition
			if (
				this.context.urlParameters &&
				this.context.urlParameters.model &&
				this.state.edition &&
				selectedEditionModel &&
				selectedEditionModel.hasDigitalManuals &&
				this.context.urlParameters &&
				this.context.urlParameters.edition &&
				!this.context.urlParameters.market
			) {
				throw SearchError.EmptyMarketParam;
			}

			// Check language set for PDF manuals
			if (
				this.context.urlParameters &&
				this.context.urlParameters.model &&
				this.state.edition &&
				selectedEditionModel &&
				!this.state.hasProductionDigitalManuals &&
				this.context.urlParameters &&
				this.context.urlParameters.edition &&
				!this.context.urlParameters.manualLang
			) {
				throw SearchError.EmptyLanguageParam;
			}

			if (selectedEditionModel) {
				if (selectedEditionModel.hasDigitalManuals) {
					const result = await this.loadMarketData(this.state.edition);
					// Check language for DM manuals after market load and validation
					if (
						result &&
						this.context.urlParameters &&
						this.context.urlParameters.model &&
						this.state.edition &&
						selectedEditionModel &&
						this.state.hasProductionDigitalManuals &&
						this.context.urlParameters &&
						this.context.urlParameters.edition &&
						this.context.urlParameters.market &&
						!this.context.urlParameters.manualLang
					) {
						throw SearchError.EmptyLanguageParam;
					} else if (!result) {
						this.context.setLoading(false);
					}
				}

				if (this.state.market || !selectedEditionModel.hasDigitalManuals) {
					const result = await this.loadLanguageData(this.state.edition, this.state.market);
					if (result && this.context.urlParameters && this.context.urlParameters.manualLang) {
						this.handleFormSubmit();
					}
				}
			}
		} catch (error) {
			this.context.setLoading(false);
			if (error === SearchError.EditionNotFound) {
				this.setState({
					formErrorType: SearchError.EditionNotFound,
					formErrorText: this.context.services.resourceProvider.getText('CS_Form_Choose_Valid_Edition'),
					hasProductionDigitalManuals: false,
				});
				this.context.setSearchData(null, null, null, false);
				this.props.onError?.(error);
				return;
			} else if (error === SearchError.EmptyMarketParam) {
				this.setState(
					{
						formErrorType: SearchError.EmptyMarketParam,
						formErrorText: this.context.services.resourceProvider.getText('CS_Form_Choose_Valid_Market'),
					},
					() => {
						this.loadMarketData(this.state.edition);
					}
				);
				this.props.onError?.(error);
				return;
			} else if (error === SearchError.EmptyLanguageParam) {
				this.setState(
					{
						formErrorType: SearchError.EmptyLanguageParam,
						formErrorText: this.context.services.resourceProvider.getText('CS_Form_Choose_Valid_Language'),
					},
					() => {
						this.loadLanguageData(this.state.edition, this.state.market);
					}
				);
				this.props.onError?.(error);
				return;
			}

			this.context.setError('Search_Error_Title', 'Search_Error_Description');
			this.props.onError?.(error);
		}
	};

	loadMarketData = async (edition: string) => {
		try {
			const markets = await this.context.getMarkets(this.props.model.modelType, edition);
			const marketOptions = markets.map<ISelectOptionProps>((x) => ({
				isSelected: this.state.market && this.state.market === x.code,
				key: x.code.toString(),
				value: this.context.services.resourceProvider.getText(x.titleResourceKey, x.titleResourceKey),
			}));
			this.setState({ markets: marketOptions });
			if (this.state.market && marketOptions.length > 0 && marketOptions.find((x) => x.isSelected) == null) {
				throw SearchError.MarketNotFound;
			}
			if (marketOptions.length === 1) {
				marketOptions[0].isSelected = true;
				this.handleMarketSubmit(marketOptions[0], true);
			}
			return true;
		} catch (error) {
			this.context.setLoading(false);
			if (error === SearchError.MarketNotFound) {
				this.setState({
					formErrorType: SearchError.MarketNotFound,
					formErrorText: this.context.services.resourceProvider.getText('CS_Form_Choose_Valid_Market'),
					language: null,
					market: null,
					languages: [],
				});
			} else {
				this.context.setError('Search_Error_Title', 'Search_Error_Description');
			}
			this.props.onError?.(error);
			return false;
		}
	};

	loadLanguageData = async (edition: string, market: string) => {
		try {
			const languages = await this.context.getLanguages(this.props.model.modelType, edition, market ? market : '');
			const languageOptions = languages.map<ISelectOptionProps>((x) => ({
				isSelected: this.state.language && (this.state.language === x.code || this.state.language === x.omsCode),
				key: x.code.toString(),
				value: x.title,
			}));
			this.setState({ languages: languageOptions });
			if (this.state.language && languageOptions.length > 0 && languageOptions.find((x) => x.isSelected) == null) {
				throw SearchError.LanguageNotFound;
			}
			return true;
		} catch (error) {
			this.context.setLoading(false);
			if (
				error === SearchError.LanguageNotFound &&
				((this.state.market && this.state.hasProductionDigitalManuals) || !this.state.hasProductionDigitalManuals)
			) {
				this.setState({
					formErrorType: SearchError.LanguageNotFound,
					formErrorText: this.context.services.resourceProvider.getText('CS_Form_Choose_Valid_Language'),
				});
				this.props.onError?.(error);
			} else if (error !== SearchError.ParametersMismatch) {
				this.context.setError('Search_Error_Title', 'Search_Error_Description');
				this.props.onError?.(error);
			}
			return false;
		}
	};

	handleProductionPeriodSubmit = (productionPeriodOption: ISelectOptionProps, isValid: boolean) => {
		if (!productionPeriodOption) {
			this.setState((prev) => ({
				...prev,
				editions: prev.editions.map<ISelectOptionProps>((e) => ({ ...e, isSelected: false })),
				edition: null,
				market: null,
				language: null,
				languages: [],
				markets: [],
				hasProductionDigitalManuals: false,
				formErrorText: null,
				formErrorType: null,
			}));
			this.props.onProductionPeriodChange(true);
			return;
		}

		const productionPeriod = this.context.editions.find((x) => x.code === productionPeriodOption.key);
		this.setState(
			(prev) => ({
				...prev,
				editions: prev.editions.map<ISelectOptionProps>((e) => ({ ...e, isSelected: e.key === productionPeriodOption.key })),
				edition: productionPeriodOption.key,
				hasProductionDigitalManuals: productionPeriod.hasDigitalManuals,
				formErrorType: SearchError.NoError,
				formErrorText: null,
				languages: [],
				language: null,
				market: null,
				markets: [],
			}),
			() => {
				if (productionPeriod.hasDigitalManuals) {
					this.loadMarketData(this.state.edition);
				} else {
					this.loadLanguageData(this.state.edition, this.state.market);
				}
				this.props.onProductionPeriodChange(productionPeriod.hasDigitalManuals);
			}
		);
	};

	handleMarketSubmit = (marketOption: ISelectOptionProps, isValid: boolean) => {
		this.setState(
			(prev) => ({
				...prev,
				markets: prev.markets.map<ISelectOptionProps>((m) => ({ ...m, isSelected: m.key === marketOption?.key })),
				market: marketOption ? marketOption.key : null,
				language: null,
				languages: [],
				formErrorType: SearchError.NoError,
				formErrorText: null,
			}),
			() => {
				if (marketOption) {
					this.loadLanguageData(this.state.edition, this.state.market);
				}
			}
		);
	};

	handleLanguageSubmit = (languageOption: ISelectOptionProps, isValid: boolean) => {
		this.setState(
			(prev) => ({
				...prev,
				languages: prev.languages.map<ISelectOptionProps>((lang) => ({ ...lang, isSelected: lang.key === languageOption?.key })),
				language: languageOption ? languageOption.key : null,
				formErrorType: SearchError.NoError,
				formErrorText: null,
			}),
			() => {
				this.context.setSearchData(this.state.edition, this.state.market, this.state.language, this.state.hasProductionDigitalManuals);
			}
		);
	};

	canBeSubmitted = () => {
		return (
			(this.state.edition && this.state.market && this.state.language) ||
			(this.state.edition && this.state.language && (!this.props.model.hasDigitalManuals || !this.state.hasProductionDigitalManuals))
		);
	};

	handleFormSubmit = () => {
		if (this.canBeSubmitted()) {
			this.props.onSubmit(
				this.state.edition ? this.state.edition : '',
				this.state.market ? this.state.market : '',
				this.state.language,
				this.state.hasProductionDigitalManuals,
				() => {
					this.context.setLoading(false);
				}
			);
		}
	};

	InfoMessage = () => {
		const infoText = this.context.services.resourceProvider.getText(`${this.props.model.modelType}_MOD_Flash_Message`, 'no_message');
		return (
			<>
				{infoText !== 'no_message' && infoText !== '' && (
					<Grid item xs={12} className={styles['info-text']}>
						<Typography style={{ fontWeight: 700, fontSize: 17, lineHeight: '21px', maxWidth: 500 }}>
							{this.context.services.resourceProvider.getText(`${this.props.model.modelType}_MOD_Flash_Message`)}
						</Typography>
					</Grid>
				)}
			</>
		);
	};

	NoMarketMessage = () => {
		const noMarketText = this.context.services.resourceProvider.getText('Form_Warning_NoMarket');
		return (
			<>
				{this.props.noMarket && (
					<div className={styles['no-market-info-message-container']}>
						<span className={styles['text']}>{noMarketText}</span>
					</div>
				)}
			</>
		);
	};

	render() {
		return (
			<React.Fragment>
				{this.props.model && this.props.model.hasDigitalManuals ? (
					<form className={styles['form']} noValidate autoComplete='off'>
						<Grid container spacing={5} className={styles['container']}>
							<this.InfoMessage />
							<Grid item lg={4} md={6} xs={12}>
								<DropdownSelect
									filterControlClassName={this.props.filterControlClassName}
									onSubmit={this.handleProductionPeriodSubmit}
									defaultValue={this.context.services.resourceProvider.getText('Period_search')}
									options={this.state.editions}
									filterControlFilledClassName={this.props.filterControllFilledClassName}
									error={this.state.formErrorType === SearchError.EditionNotFound ? this.state.formErrorText : null}
								/>
							</Grid>
							<Grid item lg={4} md={6} xs={12}>
								<DropdownSelect
									filterControlClassName={this.props.filterControlClassName}
									isDisabled={!this.state.hasProductionDigitalManuals || this.state.markets.length <= 1}
									onSubmit={this.handleMarketSubmit}
									defaultValue={this.context.services.resourceProvider.getText('Market', 'Market')}
									options={this.state.markets}
									tooltip={this.context.services.resourceProvider.getText('Market_tooltip', '')}
									filterControlFilledClassName={this.props.filterControllFilledClassName}
									error={
										this.state.formErrorType === SearchError.MarketNotFound || this.state.formErrorType === SearchError.EmptyMarketParam
											? this.state.formErrorText
											: null
									}
								/>
								<this.NoMarketMessage />
							</Grid>
							<Grid item lg={4} md={12} xs={12}>
								<DropdownSelect
									filterControlClassName={`${this.props.filterControlClassName}`}
									isDisabled={this.state.languages.length === 0}
									onSubmit={this.handleLanguageSubmit}
									defaultValue={this.context.services.resourceProvider.getText('Language', 'Language')}
									options={this.state.languages}
									filterControlFilledClassName={this.props.filterControllFilledClassName}
									error={
										this.state.formErrorType === SearchError.LanguageNotFound || this.state.formErrorType === SearchError.EmptyLanguageParam
											? this.state.formErrorText
											: null
									}
								/>
							</Grid>
							<Grid item xs={12}>
								<Fab
									id='btn-submit'
									variant='extended'
									color='primary'
									className={styles['btn-submit']}
									disabled={
										this.state.formErrorType !== SearchError.NoError ||
										this.state.edition == null ||
										(this.state.market == null && this.state.hasProductionDigitalManuals) ||
										this.state.language == null
									}
									onClick={(event) => {
										event.preventDefault();
										this.handleFormSubmit();
									}}
								>
									<span>{this.context.services.resourceProvider.getText('Show_manual')}</span>
								</Fab>
							</Grid>
						</Grid>
					</form>
				) : (
					<form className={styles['form']} noValidate autoComplete='off'>
						<Grid container spacing={5} className={`${styles['form']} ${styles['has-digital-manuals-grid']}`}>
							<this.InfoMessage />
							<Grid item lg={6} md={6} xs={12}>
								<DropdownSelect
									filterControlClassName={this.props.filterControlClassName}
									onSubmit={this.handleProductionPeriodSubmit}
									defaultValue={this.context.services.resourceProvider.getText('Period_search')}
									options={this.state.editions}
									filterControlFilledClassName={this.props.filterControllFilledClassName}
									error={this.state.formErrorType === SearchError.EditionNotFound ? this.state.formErrorText : null}
								/>
							</Grid>
							<Grid item lg={6} md={6} xs={12}>
								<DropdownSelect
									filterControlClassName={`${this.props.filterControlClassName}`}
									isDisabled={this.state.languages.length === 0}
									onSubmit={this.handleLanguageSubmit}
									defaultValue={this.context.services.resourceProvider.getText('Language', 'Language')}
									options={this.state.languages}
									filterControlFilledClassName={this.props.filterControllFilledClassName}
									error={
										this.state.formErrorType === SearchError.LanguageNotFound || this.state.formErrorType === SearchError.EmptyLanguageParam
											? this.state.formErrorText
											: null
									}
								/>
							</Grid>
							<Grid item xs={12}>
								<Fab
									id='btn-submit'
									variant='extended'
									color='primary'
									className={styles['btn-submit']}
									disabled={this.state.formErrorType !== SearchError.NoError || this.state.edition == null || this.state.language == null}
									onClick={(event) => {
										event.preventDefault();
										this.handleFormSubmit();
									}}
								>
									<span>{this.context.services.resourceProvider.getText('Show_manual')}</span>
								</Fab>
							</Grid>
						</Grid>
					</form>
				)}
			</React.Fragment>
		);
	}
}

export default withRouter(SearchForm);
